/**
 * Webinar Card Component
 *
 * Displays individual webinar with countdown timer, status badges, and action button.
 */

import { useTranslation } from 'react-i18next';
import { Video, Crown, Calendar, Clock } from 'lucide-react';
import { getLocalizedTitle, useCountdown, useWebinarStatus, type Webinar } from '@immopersian/common';

interface WebinarCardProps {
  webinar: Webinar;
  onClick: () => void;
}

export function WebinarCard({ webinar, onClick }: WebinarCardProps) {
  const { t, i18n } = useTranslation();
  const timeLeft = useCountdown(webinar.start_date);
  const status = useWebinarStatus(webinar);
  const isPaid = !!webinar.price_id;

  // Dates for display
  const startDate = new Date(webinar.start_date);
  const endDate = webinar.end_date ? new Date(webinar.end_date) : null;

  // Don't show expired webinars
  if (status === 'expired') {
    return null;
  }

  return (
    <div
      onClick={onClick}
      className="relative flex-shrink-0 w-full cursor-pointer group"
    >
      <div className="bg-gradient-to-r from-brand-dark to-brand-dark/90 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-brand-terracotta/20 hover:border-brand-terracotta">
        <div className="flex flex-col md:flex-row">
          {/* Left: Thumbnail or gradient */}
          <div className="relative w-full md:w-64 h-48 md:h-auto bg-gradient-to-br from-brand-terracotta/30 to-brand-gold/20 flex-shrink-0">
            {webinar.thumbnail_url ? (
              <img
                src={webinar.thumbnail_url}
                alt={getLocalizedTitle(webinar, i18n.language)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Video className="w-20 h-20 text-white/40" />
              </div>
            )}

            {/* Live badge */}
            {status === 'live' && (
              <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2 animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                LIVE NOW
              </div>
            )}

            {/* Paid badge */}
            {isPaid && (
              <div className="absolute top-3 right-3 bg-brand-gold text-brand-dark px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {t('webinar.paid', 'Paid')}
              </div>
            )}

            {/* Free badge */}
            {!isPaid && (
              <div className="absolute top-3 right-3 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                {t('webinar.free', 'Free')}
              </div>
            )}
          </div>

          {/* Right: Content */}
          <div className="flex-1 p-6 flex flex-col justify-between">
            <div>
              {/* Title */}
              <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-brand-gold transition-colors">
                {getLocalizedTitle(webinar, i18n.language)}
              </h3>

              {/* Date and time */}
              <div className="flex flex-wrap gap-4 text-sm text-white/70 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {startDate.toLocaleDateString(i18n.language, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {startDate.toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {endDate && (
                    <>
                      {' → '}
                      {endDate.toLocaleTimeString(i18n.language, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </>
                  )}
                </div>
              </div>

              {/* Countdown, Live, Replay, or Processing indicator */}
              {status === 'live' ? (
                <div className="bg-red-600/20 border-2 border-red-500 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-400 mb-1">
                    {t('webinar.happeningNow', 'HAPPENING NOW')}
                  </div>
                  <div className="text-white/80 text-sm">
                    {t('webinar.clickToJoin', 'Click to join the webinar')}
                  </div>
                </div>
              ) : status === 'replay' ? (
                <div className="bg-brand-gold/20 border-2 border-brand-gold rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-brand-gold mb-1">
                    {t('webinar.recordingAvailable', 'Recording Available')}
                  </div>
                  <div className="text-white/80 text-sm">
                    {t('webinar.watchRecording', 'Watch the recording of this webinar')}
                  </div>
                </div>
              ) : status === 'processing' ? (
                <div className="bg-blue-600/20 border-2 border-blue-400 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    {t('webinar.recordingProcessing', 'Recording Processing')}
                  </div>
                  <div className="text-white/80 text-sm">
                    {t('webinar.recordingProcessingMessage', 'The recording will be available soon')}
                  </div>
                </div>
              ) : (
                <div className="bg-brand-terracotta/20 border-2 border-brand-terracotta rounded-lg p-4">
                  <div className="text-white/70 text-sm mb-2 text-center">
                    {t('webinar.startsIn', 'Starts in')}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {timeLeft.days !== undefined && timeLeft.days > 0 && (
                      <div>
                        <div className="text-3xl font-bold text-brand-gold">
                          {timeLeft.days}
                        </div>
                        <div className="text-xs text-white/60">
                          {t('webinar.days', 'Days')}
                        </div>
                      </div>
                    )}
                    {timeLeft.hours !== undefined && (
                      <div>
                        <div className="text-3xl font-bold text-brand-gold">
                          {timeLeft.hours.toString().padStart(2, '0')}
                        </div>
                        <div className="text-xs text-white/60">
                          {t('webinar.hours', 'Hours')}
                        </div>
                      </div>
                    )}
                    {timeLeft.minutes !== undefined && (
                      <div>
                        <div className="text-3xl font-bold text-brand-gold">
                          {timeLeft.minutes.toString().padStart(2, '0')}
                        </div>
                        <div className="text-xs text-white/60">
                          {t('webinar.minutes', 'Min')}
                        </div>
                      </div>
                    )}
                    {timeLeft.seconds !== undefined && (
                      <div>
                        <div className="text-3xl font-bold text-brand-gold">
                          {timeLeft.seconds.toString().padStart(2, '0')}
                        </div>
                        <div className="text-xs text-white/60">
                          {t('webinar.seconds', 'Sec')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action button */}
            <button
              className={`mt-4 w-full text-white font-semibold py-3 px-6 rounded-lg transition-colors ${
                status === 'processing'
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-brand-terracotta hover:bg-brand-terracotta/90'
              }`}
              disabled={status === 'processing'}
            >
              {status === 'live'
                ? t('webinar.joinNow', 'Join Now')
                : status === 'replay'
                ? t('webinar.watchRecording', 'Watch Recording')
                : status === 'processing'
                ? t('webinar.waitingForRecording', 'Waiting for Recording')
                : t('webinar.learnMore', 'Learn More')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
